import React from "react";
import { Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import { ArrowLeft, ShieldCheck } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-display text-2xl text-[#F5F2EE] mb-3">{title}</h2>
    <div className="text-[#DCD7D2] text-sm leading-relaxed space-y-3">{children}</div>
  </section>
);

const Privacy = () => (
  <div className="min-h-screen bg-[#0C0E12]" data-testid="privacy-page">
    <SiteHeader />
    <main className="max-w-3xl mx-auto px-6 md:px-12 py-12">
      <Link to="/" className="inline-flex items-center gap-2 text-[#A29F9B] hover:text-[#C5A880] mb-8">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-[#C5A880]" />
        <span className="label-uppercase">RGPD</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl mb-2">Política de Privacidade</h1>
      <p className="text-[#A29F9B] mb-10">Última atualização: 28 de Abril de 2026</p>

      <Section title="1. Responsável pelo tratamento">
        <p>
          <strong>Solfut, Lda</strong>, pessoa colectiva nº 503 928 518, com sede em Edifício Camões,
          Rua de Camões nº 117, 6º andar, 4000-144 Porto, Portugal.
          Encarregado da Protecção de Dados:
          {" "}<a className="text-[#C5A880] hover:underline" href="mailto:dpo@solfut.com">dpo@solfut.com</a>.
        </p>
      </Section>

      <Section title="2. Que dados recolhemos">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Conta</strong>: email, nome, idade, género, orientação, fotografias.</li>
          <li><strong>Questionário</strong>: respostas a 45 perguntas sobre personalidade, valores, preferências.</li>
          <li><strong>Faturação</strong>: nome, NIF (opcional), morada, código postal, localidade — para emissão de Fatura+Recibo Moloni.</li>
          <li><strong>Pagamentos</strong>: processados pela EuPago (SIBS); <strong>não armazenamos dados de cartão</strong>.</li>
          <li><strong>Encontros</strong>: registos do diário (quando criados), com visibilidade que tu controlas (privado / partilhado).</li>
          <li><strong>Pesquisa pública</strong>: apenas com o teu consentimento explícito, fazemos pesquisa em fontes públicas (Tavily) com finalidade de verificação cruzada.</li>
          <li><strong>Técnicos</strong>: IP, user-agent, logs de acesso (mantidos 30 dias).</li>
        </ul>
      </Section>

      <Section title="3. Para que usamos">
        <ul className="list-disc pl-5 space-y-1">
          <li>Operar o algoritmo de matchmaking baseado em afinidades.</li>
          <li>Processar pagamentos e emitir Fatura+Recibo (obrigação legal).</li>
          <li>Enviar notificações relevantes (ligações mútuas, mensagens, alertas de segurança).</li>
          <li>Prevenir fraude, abuso e cumprir obrigações legais.</li>
        </ul>
      </Section>

      <Section title="4. Fundamento legal">
        <p>
          (a) <strong>Execução do contrato</strong> (Termos e Condições) para conta + matchmaking + faturação.
          (b) <strong>Consentimento</strong> para envio de comunicações de marketing e para pesquisa pública Tavily — revogável a qualquer momento.
          (c) <strong>Obrigação legal</strong> para faturação SAF-T / Autoridade Tributária.
          (d) <strong>Interesse legítimo</strong> para prevenir fraude e abuso.
        </p>
      </Section>

      <Section title="5. Com quem partilhamos">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>EuPago</strong> (processamento de pagamentos, SIBS/Portugal — certificado PSD2).</li>
          <li><strong>Moloni</strong> (sistema certificado de faturação, Portugal).</li>
          <li><strong>Anthropic</strong> e <strong>OpenAI</strong> (análise IA, opt-in pelo plano Premium; sem treino com os teus dados).</li>
          <li><strong>Tavily</strong> (pesquisa pública, opt-in explícito por utilizador-alvo).</li>
          <li><strong>SMTP provider</strong> (envio de emails transaccionais).</li>
          <li><strong>Autoridades</strong> apenas mediante ordem judicial.</li>
        </ul>
      </Section>

      <Section title="6. Quanto tempo guardamos">
        <ul className="list-disc pl-5 space-y-1">
          <li>Conta activa: enquanto a manténs.</li>
          <li>Após eliminação: <strong>anonimização imediata</strong> dos dados pessoais, excepto faturação (mantida 10 anos por imposição fiscal).</li>
          <li>Logs técnicos: 30 dias.</li>
          <li>Cache de pesquisa pública: 30 dias.</li>
        </ul>
      </Section>

      <Section title="7. Os teus direitos">
        <p>
          Tens direito a: <strong>acesso, rectificação, eliminação ("apagar conta"), portabilidade, limitação, oposição</strong>
          {" "}e a <strong>reclamar</strong> junto da Comissão Nacional de Protecção de Dados
          {" "}(<a className="text-[#C5A880] hover:underline" href="https://www.cnpd.pt" target="_blank" rel="noopener noreferrer">www.cnpd.pt</a>).
          Para exercer, escreve a dpo@solfut.com — respondemos em até 30 dias.
        </p>
      </Section>

      <Section title="8. Cookies">
        <p>
          A Soulmatch usa apenas cookies <strong>técnicos essenciais</strong> (sessão de login).
          Não usamos cookies de marketing nem trackers de terceiros.
        </p>
      </Section>

      <Section title="9. Segurança">
        <p>
          Palavras-passe armazenadas com bcrypt; comunicação via TLS;
          dados de cartão não tocam nos nossos servidores. Em caso de violação de dados,
          notificamos a CNPD em até 72h e os utilizadores afectados sem demora.
        </p>
      </Section>

      <p className="text-xs text-[#A29F9B] mt-12 pt-6 border-t border-white/5">
        © 2026 Solfut, Lda · Versão 1.0 · 28-04-2026
      </p>
    </main>
    <Footer />
    </div>
);

export default Privacy;
